import { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Text, Flex, useToast, Skeleton, Switch, Icon } from '@chakra-ui/react';
import { PageContent } from '@/components/PageContent';
import { SectionBox } from '@/components/SectionBox';
import { breadcrumb } from './breadcrumb';
import { ShortcutCard } from '../Home/components/ShortcutCard';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { useAppApplicationComponentArea, useEnableApplicationComponentMutation } from '@/services/queries/admin-config';
import { ApplicationComponentId } from '@/model/application-component';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { ReactComponent as PostIcon } from '@/assets/icons/post.svg';
import { ReactComponent as PollIcon } from '@/assets/icons/poll.svg';
import { ReactComponent as GuessIcon } from '@/assets/icons/guess.svg';
import { useTranslation } from 'react-i18next';

type Link = {
  label: string;
  url: string;
  icon: ReactNode;
};
const links: Array<Link> = [
  {
    label: 'pages.interactive_area.interactive_area_posts_shortcut',
    url: '/application/interactive-area/posts',
    icon: <Icon as={PostIcon} color='brand.500' w='24px' h='24px' />,
  },
  // {
  //   label: 'pages.interactive_area.interactive_area_quizzes_shortcut',
  //   url: '/application/interactive-area/quizzes',
  //   icon: <Icon as={QuizIcon} color='brand.500' w='24px' h='24px' />,
  // },
  {
    label: 'pages.interactive_area.interactive_area_polls_shortcut',
    url: '/application/interactive-area/polls',
    icon: <Icon as={PollIcon} color='brand.500' w='24px' h='24px' />,
  },
  {
    label: 'pages.interactive_area.interactive_area_predictions_shortcut',
    url: '/application/interactive-area/predictions',
    icon: <Icon as={GuessIcon} color='brand.500' w='24px' h='24px' />,
  },
];

export const InteractiveArea = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [isAppAreaActive, setIsAppAreaActive] = useState(false);

  const { data: appAreaData, isLoading: isLoadingAppArea } = useAppApplicationComponentArea(
    undefined,
    ApplicationComponentId.INTERACTIVE_AREA,
  );
  const { mutate: enableAppAreaMutation, isLoading: isLoadingEnableOrDisableAppArea } = useEnableApplicationComponentMutation();
  const [pendingAppAreaState, setPendingAppAreaState] = useState<boolean | null>(null);
  const [isDialogAppAreaState, setIsDialogAppAreaState] = useState(false);

  const cancelAppAreaChange = () => {
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const handleAppAreaChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setPendingAppAreaState(isChecked);
    setIsDialogAppAreaState(true);
  };

  const confirmAppAreaChange = () => {
    if (pendingAppAreaState === null) return;

    const successActionString = pendingAppAreaState
      ? t('pages.interactive_area.interactive_area_enable_success')
      : t('pages.interactive_area.interactive_area_disable_success');
    const errorActionString = pendingAppAreaState
      ? t('pages.interactive_area.interactive_area_enable_error')
      : t('pages.interactive_area.interactive_area_disable_error');

    setIsAppAreaActive(pendingAppAreaState);

    enableAppAreaMutation(
      [
        { component_id: ApplicationComponentId.POLLS, active: pendingAppAreaState },
        { component_id: ApplicationComponentId.POSTS, active: pendingAppAreaState },
        { component_id: ApplicationComponentId.PREDICTIONS, active: pendingAppAreaState },
        { component_id: ApplicationComponentId.QUIZZES, active: pendingAppAreaState },
      ],
      {
        onSuccess: () => {
          toast({
            status: 'success',
            title: successActionString,
          });
        },
        onError: () => {
          toast({
            status: 'error',
            title: errorActionString,
          });

          setIsAppAreaActive(!pendingAppAreaState);
        },
      },
    );

    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  useEffect(() => {
    if (!appAreaData) return;

    setIsAppAreaActive(appAreaData.is_active);
  }, [appAreaData]);

  return (
    <PageContent title={t('pages.interactive_area.interactive_area_page_title')} breadcrumbItems={breadcrumb}>
      <Flex direction='column' gap='small'>
        <SectionBox bodyProps={{ gap: 'small' }}>
          <Flex alignItems='center' gap={4}>
            {isLoadingAppArea ? (
              <>
                <Skeleton w='130px' h='21px' />
                <Skeleton w='102px' h='21px' />
              </>
            ) : (
              <>
                <Text fontWeight='bold' color='brand.500'>
                  {t('pages.interactive_area.interactive_area_section_title')}
                </Text>

                <Switch size='sm' isChecked={isAppAreaActive} onChange={handleAppAreaChange}>
                  {isAppAreaActive
                    ? t('pages.interactive_area.interactive_area_enabled_label')
                    : t('pages.interactive_area.interactive_area_disabled_label')}
                </Switch>
              </>
            )}
          </Flex>

          <Flex gap='xxsmall' flexWrap='wrap'>
            {links?.map(link => (
              <Link key={link.url} to={link.url}>
                <ShortcutCard label={t(link.label)} icon={link.icon} />
              </Link>
            ))}
          </Flex>
        </SectionBox>
      </Flex>

      <ConfirmationModal
        isOpen={isDialogAppAreaState}
        onClose={cancelAppAreaChange}
        onConfirm={confirmAppAreaChange}
        title={t('pages.interactive_area.interactive_area_modal_title')}
        description={
          <Text>
            {pendingAppAreaState
              ? t('pages.interactive_area.interactive_area_modal_confirm_prefix')
              : t('pages.interactive_area.interactive_area_modal_confirm_disabled')}
          </Text>
        }
      />

      <CenteredSpinner isLoading={isLoadingEnableOrDisableAppArea} />
    </PageContent>
  );
};
